/**
 * @fileoverview One job of the one to-do list (D51): the same card on Today
 * and at the top of Plants (owner, 2026-10-15, the approved mock-ups).
 *
 * A tick circle, the job's title verb first ("Cover 9 plants"), one reason
 * line ("Sun night freeze, 29°F"), a "when" pill ("Today", "Tonight", "In 2
 * days", "This week") with a calm time-left bar under a coming job, and one
 * disclosure (owner, 2026-10-16: Why? and the plants button were two, and
 * said things twice). Its button says what is inside, the same words open
 * and closed, only the chevron turns: "Details ⌄" on every card (owner,
 * 2026-10-16, option A of two mock-ups; it said "9 plants in 2 beds" on some
 * cards and "Why" on others, which opened the same things). Open, it shows the
 * why lines first (the readings over a run, what to do, the advice), then the
 * plants by bed ("Bed A · 5"), each tickable and each name a link to its card
 * ("Romaine lettuce · about ½″" on a water card), then "2 of 9 covered" and
 * "Mark all covered", and last "Not today" and "Skip it" for a job the
 * gardener will not do today. A job with neither has no button, and its "Not
 * today" and "Skip it" sit on the card itself (owner, 2026-10-16). The circle
 * only ticks the whole job; the button only opens it. A cover job (a frost, a
 * freeze, a cold night) has a blue left edge.
 *
 * Skipped, it stays in its place that day too, struck through, with "Skipped.
 * Grow won't ask about this one again." and Undo (owner, 2026-10-16). A tick
 * or a skip shows at once (pending, useTodoMarks) and stays shown once saved
 * until the brief read after it says it (B49).
 *
 * Ticked, the job stays in its place, struck through, with what the tick
 * wrote ("Covered, noted on all 9 plants. Undo"), or for a planting job
 * what it recorded and when, easy to change ("Started · Today ⌄ · Undo"):
 * the day in the server's words (planting.onText), the date box between the
 * plants' last stage and the garden's today (planting.notBefore, latest), so
 * the card works out no day from the phone (the to-do rebuild, batch 5).
 *
 * A bed's note on a job (the server's, plants.beds[].note: "Run the soaker
 * hose or drip." for a bed watered that way) is said with its bed: under the
 * bed's name in the plant list, or on the face when the job is all in one bed
 * (a bed's own card shows only its part; the audit, 2026-10-16).
 *
 * A When you can job (owner, 2026-10-17: a planting window, a once-a-season
 * chore) wears the quiet grey pill ("Until Oct 31"), and its Postpone opens
 * the server's choices of day (Tomorrow, In a week, In its last week, Pick a
 * day when the server offers one) in a sheet; a job the server gives no
 * choices (postpone null) moves to tomorrow at once.
 *
 * A panel rule's job says its rule's advice under its reason, on the face
 * ("Scatter it over empty beds and rake it in."; the server's tip), as To do
 * did before D51 (the audit, 2026-10-16).
 *
 * A bed's job (D55) says under its reason how its covers stand against the
 * weather, in the server's words: "✓ Your light frost cloth is enough for
 * tonight" in green, what to do when it is not in the warm red, a note
 * ("Weigh the cover down") plain. A bed with no cover said gets the plain
 * job (owner, 2026-10-16: "Set its covers" was confusing, and is gone).
 *
 * On Plants (compact) there is no disclosure: a tap on the card shows just
 * its plants' cards below. A job left from yesterday is tapped to choose: do
 * it today, move it to tomorrow, or skip it.
 *
 * A gardener's own to-do (kind own, D50; Fable pass 2) is the same card: its
 * title as typed, the server's reason line (the plant's or bed's name, "added
 * Mon" past its day, "added by Sam"), its pill ("Until Sun", "Last day",
 * "Since Oct 20"), and a foot of three equal buttons, Edit · Postpone · Skip
 * it (onEdit opens the own to-do sheet), with no Details.
 *
 * Every word is the server's (the brief's todo); this lays the job out.
 *
 * Where: TodoGroups (Today), PlantJobs (the top of Plants), the Beds page
 * (features/locations BedJobs, D56: flat, inside each bed's card; the bed's
 * pill uses WHEN_TONE).
 * API: none; marks are the page's (useTodoMarks).
 * Words (en/es): todo.tick, todo.tickPlant, todo.notToday, todo.later
 * (aria), todo.details, todo.section.*, todo.allMarked, todo.own.edit,
 * today.planting.changeDay (aria); every other word is the server's.
 *
 * @module JobCard
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronDown, Clock, Pencil, SkipForward } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { Input } from '@/shared/components/ui/input';
import { NameParts } from '@/shared/components/NameParts';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { TileButton } from '@/shared/components/ui/TileButton';
import { FailureText } from '@/shared/components/ServerFailure';
import type { BriefTodo, TodoJob } from '@/shared/api/todayBrief';
import type { TodoMark } from '@/shared/api/todo';
import type { PendingTick } from '../lib/useTodoMarks';

/** The when pill's colours, by its tone: today's green, the cold blue, the calm amber (also a bed's pill on the Beds page, D56). */
export const WHEN_TONE: Record<TodoJob['when']['tone'], string> = {
  today: 'bg-primary text-primary-foreground',
  cold: 'bg-verdict-cold-tint text-verdict-cold',
  soon: 'bg-verdict-warm-tint text-verdict-warm',
  // A When you can job's (owner, 2026-10-17): the quietest, after blue and amber.
  calm: 'bg-muted text-foreground',
};

/** The time-left bar's lit segments, by tone. */
const LEFT_TONE: Record<TodoJob['when']['tone'], string> = {
  today: 'bg-primary',
  cold: 'bg-verdict-cold',
  soon: 'bg-verdict-warm',
  calm: 'bg-muted-foreground',
};

/** A cover check's colour, by its tone (D55): enough in green, not enough in the warm red, a note plain. */
const CHECK_TONE: Record<'ok' | 'short' | 'note', string> = {
  ok: 'text-verdict-good',
  short: 'text-destructive',
  note: 'text-muted-foreground',
};

/** The card: the theme's card colours, rounded, a thumb's room inside. */
const CARD = 'rounded-xl border border-border bg-card';

/** A part's label inside the open card: "WHY", "PLANTS" (option A, owner, 2026-10-16). */
const SECTION_LABEL = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

/** What a card shows and does. */
export interface JobCardProps {
  /** The job, as the server says it. */
  job: TodoJob;
  /** The list's own words (Why?, Undo, the later choices). */
  words: BriefTodo['words'];
  /** A tick on its way, shown already. */
  pending?: PendingTick;
  /** A mark is on its way for this job. */
  busy: boolean;
  /** What to say under it when its last mark did not save; null for nothing. */
  failedWords: string | null;
  /** The top of Plants: no disclosure; a tap selects it. */
  compact?: boolean;
  /** Its disclosure (why lines and plants) is open (Today); the card keeps its own when the page does not. */
  open?: boolean;
  /** Opens or closes its disclosure. */
  onOpenChange?: (open: boolean) => void;
  /** It is the one the page below is narrowed to (Plants). */
  selected?: boolean;
  /** A tap on the card (Plants: show its plants). */
  onSelect?: () => void;
  /** Makes a mark on it. */
  onMark: (mark: TodoMark) => void;
  /** A job left from yesterday was tapped: offer today, tomorrow or skip. */
  onLater?: () => void;
  /** Its element, to scroll to it (an email's link). */
  cardRef?: React.Ref<HTMLDivElement>;
  /** Drawn inside a bed's card (the Beds page, D56): no card, edge or shadow of its own; the bed's card has them. */
  flat?: boolean;
  /** A gardener's own to-do (D50): Edit joins the foot, opening the own to-do sheet; none for any other job. */
  onEdit?: () => void;
}

/**
 * A round tick: filled with a check when done (also an own to-do's compact row on a plant's or bed's card, D50).
 *
 * @param props.done - Ticked.
 * @param props.small - A plant's, in the list.
 */
export const TickCircle: React.FC<{ done: boolean; small?: boolean }> = ({ done, small = false }) => (
  <span
    className={cn(
      'flex shrink-0 items-center justify-center rounded-full border-2 transition-colors',
      small ? 'h-5 w-5' : 'h-[1.625rem] w-[1.625rem]',
      done ? 'border-primary bg-primary text-primary-foreground' : 'border-primary bg-background',
    )}
    aria-hidden="true"
  >
    {done && <Check className={small ? 'h-3 w-3' : 'h-4 w-4'} strokeWidth={3} />}
  </span>
);

/**
 * One job's card.
 *
 * @param props - JobCardProps.
 */
export const JobCard: React.FC<JobCardProps> = ({
  job, words, pending, busy, failedWords, compact = false, open = false, onOpenChange, selected = false, onSelect, onMark, onLater, cardRef, flat = false, onEdit,
}) => {
  const { reactTranslate: t } = useTranslation();
  /** The disclosure's own state, for a page that does not keep it (Plants keeps none; Today keeps it by job). */
  const [ownOpen, setOwnOpen] = React.useState(false);
  const isOpen = onOpenChange ? open : ownOpen;
  /**
   * Opens or closes the disclosure, wherever its state is kept.
   *
   * @param next - Open.
   */
  const setOpen = (next: boolean): void => { if (onOpenChange) onOpenChange(next); else setOwnOpen(next); };
  const done = pending?.done ?? job.done;
  /**
   * Skipped today: in place, struck through, said so, with Undo (owner, 2026-10-16: "skip it is scary - it just
   * disappears"); a skip saved shows so at once, before the brief read after it says it (B49).
   */
  const skipped = pending?.skipped ?? job.skipped;
  /** The line under it once done or skipped: the server's, or a saved skip's line from its answer until the brief says it. */
  const doneLine = skipped && pending?.line ? pending.line : job.doneLine;
  /**
   * Whether a plant row shows ticked: as the server says, or as a tick on its way says.
   *
   * @param ids - The row's plants.
   * @param serverDone - The server's answer for the row.
   */
  const rowDone = (ids: number[], serverDone: boolean): boolean => {
    if (pending?.done !== undefined) return pending.done;
    if (pending?.plantIds && ids.every((id) => pending.plantIds?.includes(id))) return Boolean(pending.plantsDone);
    return serverDone;
  };
  const plants = job.plants;
  /** The note of the job's one bed, said on the face; a job in several beds says each under its bed in the list. */
  const soleBedNote = plants && plants.beds.length === 1 ? plants.beds[0].note ?? null : null;
  const canTick = job.tickable && !busy;
  const hasWhy = !compact && job.why.length > 0;
  /** The plants by bed are listed when the job has more than one. */
  const hasList = !compact && Boolean(plants && plants.count > 1);
  /** One disclosure for both; its button says the plants when there is a list, else "Why". */
  // It stays when the job is done, so ticking the last plant (or Mark all) does not close it under the gardener's thumb
  // (owner, 2026-10-16: "the screen jumps because the drop down closes ... fix that so it stays open"); a skipped job has none.
  const hasDisclosure = (hasWhy || hasList) && !skipped;
  const showLater = !compact && !done && !job.missed;
  /** A When you can job's Postpone choices are open (its sheet). */
  const [postponing, setPostponing] = React.useState(false);
  /** The day picked in Postpone's sheet, "YYYY-MM-DD", or '' for none yet. */
  const [pickedDay, setPickedDay] = React.useState('');
  const postpone = job.postpone ?? null;

  /** The day a planting job was done, in the server's words ("Today", "Oct 3"). */
  const doneDay = job.planting?.onText ?? job.planting?.on ?? '';

  /**
   * Postpone and Skip it, the card's quiet foot: two equal buttons with their icons under a hairline, inside the
   * disclosure or on the card when it has nothing to open (owner, 2026-10-16: "Mark, skip, postpone. It all looks like
   * an afterthought"; they were two underlined links). An own to-do's has Edit first, three equal buttons (D50, Fable
   * pass 2: the lone Edit line made the card a third taller).
   */
  const laterRow = (
    <div className={cn('mt-3 grid gap-2 border-t pt-3', onEdit ? 'grid-cols-3' : 'grid-cols-2')}>
      {onEdit && (
        <TileButton size="small" onClick={onEdit} disabled={busy} icon={<Pencil />}>
          {t('todo.own.edit')}
        </TileButton>
      )}
      {/* A When you can job's Postpone offers days (its sheet); any other moves it to tomorrow at once. */}
      <TileButton size="small" onClick={() => (postpone ? setPostponing(true) : onMark({ action: 'tomorrow' }))} disabled={busy} icon={<Clock />}>
        {t('todo.notToday')}
      </TileButton>
      <TileButton size="small" onClick={() => onMark({ action: 'skip' })} disabled={busy} icon={<SkipForward />}>
        {words.later.skip}
      </TileButton>
    </div>
  );

  const title = job.link && !done
    ? <Link to={job.link} state={{ from: 'today' }} className="underline-offset-4 hover:underline">{job.title}</Link>
    : job.title;

  /** When it is for ("Today", "In 2 days"), floated on the title's first line with the title wrapping around it (owner, 2026-10-15: "make the text flow all the way across so it takes up the blank space"; 2026-10-16: "wrap the text around the pill so it flows"). */
  const whenPill = (
    <span className={cn('mt-0.5 inline-block shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-[0.8125rem] font-semibold leading-none', WHEN_TONE[job.when.tone], done && 'opacity-60')}>
      {job.when.text}
    </span>
  );

  /** The card's body: a button on Plants (to show its plants) and for a missed job (to choose), else plain. */
  const body = (
    <>
      {/* The pill floats at the right and the title wraps around it, using the room under it (owner, 2026-10-16: "wrap the text around the pill so it flows"). */}
      <span className="block">
        <span className="float-right mb-1 ml-2">{whenPill}</span>
        <span className={cn('text-[1.0625rem] font-semibold leading-snug transition-colors duration-300', done && 'text-muted-foreground line-through')}>{title}</span>
      </span>
      {job.reason && <span className="mt-0.5 block text-sm leading-snug text-muted-foreground">{job.reason}</span>}
      {/* A garden chore's own sentence, under its reason, as To do said it (the audit, 2026-10-16). */}
      {job.tip && !done && <span className="mt-0.5 block text-sm leading-snug text-foreground">{job.tip}</span>}
      {/* A job all in one bed says that bed's note on its face ("Run the soaker hose or drip."). */}
      {soleBedNote && !done && <span className="mt-0.5 block text-sm leading-snug text-foreground">{soleBedNote}</span>}
      {!done && job.checks.map((check) => (
        <span key={check.text} className={cn('mt-1 block text-sm leading-snug', CHECK_TONE[check.tone] ?? CHECK_TONE.note)}>{check.text}</span>
      ))}
    </>
  );

  return (
    <div
      ref={cardRef}
      className={cn(
        !flat && CARD,
        'grid grid-cols-[2.75rem_minmax(0,1fr)] items-start gap-x-1.5 py-2.5 pl-1 pr-3',
        !flat && job.edge && !done && 'border-l-[5px] border-l-verdict-cold',
        selected && 'ring-2 ring-primary',
        done && 'opacity-70',
      )}
    >
      {/* The circle ticks the whole job; a thumb's 44px around the circle that shows. */}
      <button
        type="button"
        onClick={() => onMark({ action: done ? 'undone' : 'done' })}
        disabled={!canTick || skipped}
        aria-pressed={done}
        aria-label={t('todo.tick', { job: job.title })}
        className="-my-1.5 flex h-11 w-11 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
      >
        <TickCircle done={done && !skipped} />
      </button>

      <div className="min-w-0 pt-0.5">
        {onSelect || (job.missed && onLater) ? (
          <button
            type="button"
            onClick={job.missed && onLater ? onLater : onSelect}
            aria-pressed={onSelect && !job.missed ? selected : undefined}
            aria-label={job.missed ? t('todo.later', { job: job.title }) : undefined}
            className="block w-full rounded-sm text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {body}
          </button>
        ) : body}

        {/* How long is left, calmly: a segment a day, under the reason it belongs to (owner, 2026-10-16: it sat between the
            plants button and the open card, detached). */}
        {job.left && !done && (
          <span className="mt-1.5 flex gap-0.5" aria-hidden="true">
            {Array.from({ length: job.left.of }, (_, i) => (
              <span key={i} className={cn('h-1 w-4 rounded-full', i < (job.left?.on ?? 0) ? LEFT_TONE[job.when.tone] : 'bg-border')} />
            ))}
          </span>
        )}

        {/* The one button: what is inside ("4 plants in 2 beds", or "Why"), the same words open and closed; only the chevron turns. */}
        {hasDisclosure && (
          <button
            type="button"
            onClick={() => setOpen(!isOpen)}
            aria-expanded={isOpen}
            className="mt-1.5 inline-flex min-h-9 items-center gap-1 whitespace-nowrap rounded-full border bg-background px-2.5 text-sm font-semibold text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {/* The same word on every card, open or closed (owner, 2026-10-16, option A: "Why?" on some cards and "3 plants" on others opened the same things). */}
            {t('todo.details')}
            <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', isOpen && 'rotate-180')} aria-hidden="true" />
          </button>
        )}

        {/* Nothing to open (no why lines, one plant or none: a garden chore): Not today and Skip it on the card itself, so
            every job can be put off or skipped (owner, 2026-10-16: "why do rules based jobs not allow a postpone or skip?"). */}
        {!hasDisclosure && showLater && laterRow}

        {failedWords && <p className="mt-1 text-xs text-destructive" role="alert"><FailureText text={failedWords} /></p>}

        {/* Ticked: what it wrote, and Undo; a planting job what it recorded, from which day (easy to change). */}
        {done && job.planting?.on && (
          <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-verdict-good">
            <span>{job.planting.done}</span>
            <span aria-hidden="true">·</span>
            <span className="relative inline-flex min-h-9 items-center gap-0.5 font-semibold text-foreground underline underline-offset-2">
              {doneDay}
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
              <Input
                type="date"
                value={job.planting.on}
                min={job.planting.notBefore ?? undefined}
                max={job.planting.latest ?? undefined}
                onChange={(event) => { if (event.target.value) onMark({ action: 'day', day: event.target.value }); }}
                disabled={busy}
                aria-label={t('today.planting.changeDay', { date: doneDay })}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              />
            </span>
            <span aria-hidden="true">·</span>
            <button type="button" onClick={() => onMark({ action: 'undone' })} disabled={busy} className="inline-flex min-h-9 items-center font-semibold text-foreground underline underline-offset-2">
              {words.undo}
            </button>
          </p>
        )}
        {done && !job.planting && !compact && (
          <p className={cn('mt-1 flex flex-wrap items-center gap-1.5 text-xs', skipped ? 'text-muted-foreground' : 'text-verdict-good')}>
            {doneLine && <span>{doneLine}</span>}
            <button type="button" onClick={() => onMark({ action: skipped ? 'unskip' : 'undone' })} disabled={busy} className="inline-flex min-h-9 items-center font-semibold text-foreground underline underline-offset-2">
              {words.undo}
            </button>
          </p>
        )}
      </div>

      {/* Open: the why lines (the readings, what to do, the advice), then the plants by bed, each its own tick, how many and Mark all, then a way to leave it for today. */}
      {hasDisclosure && isOpen && (
        <div className="col-start-2 col-end-3 mt-2">
          {/* Why: the readings, what to do, the advice, under its label. */}
          {hasWhy && (
            <section aria-label={t('todo.section.why')}>
              <p className={SECTION_LABEL}>{t('todo.section.why')}</p>
              <div className="mt-1.5 space-y-1.5 rounded-lg bg-verdict-cold-tint/60 px-3 py-2.5 text-sm leading-snug text-foreground">
                {job.why.map((line) => <p key={line}>{line}</p>)}
              </div>
            </section>
          )}
          {/* Plants: how many are done at the label's right, the plants by bed, Mark all. */}
          {hasList && plants && (
            <section aria-label={t('todo.section.plants')} className={cn(hasWhy && 'mt-3')}>
              <p className={cn(SECTION_LABEL, 'flex items-baseline justify-between gap-2')}>
                <span>{t('todo.section.plants')}</span>
                <span className="font-normal normal-case tracking-normal">{plants.doneText}</span>
              </p>
              {plants.beds.map((bed) => (
                <div key={bed.id ?? 'none'}>
                  <p className="mt-2 text-[0.8125rem] font-semibold text-muted-foreground">{bed.heading}</p>
                  {/* The bed's own note, with its bed ("Run the soaker hose or drip."; the audit, 2026-10-16). */}
                  {plants.beds.length > 1 && bed.note && <p className="text-sm leading-snug text-foreground">{bed.note}</p>}
                  <ul>
                    {bed.rows.map((row) => {
                      const ticked = rowDone(row.ids, row.done);
                      return (
                        <li key={row.text} className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => onMark({ action: ticked ? 'undone' : 'done', plantIds: row.ids })}
                            disabled={!job.perPlant || busy}
                            aria-pressed={ticked}
                            aria-label={t('todo.tickPlant', { plant: row.text })}
                            className="-ml-2.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                          >
                            <TickCircle done={ticked} small />
                          </button>
                          <Link to={row.link} className={cn('text-[0.9375rem] underline decoration-muted-foreground/40 underline-offset-4', ticked && 'text-muted-foreground line-through')}>
                            <NameParts parts={row.parts} boldClassName="font-semibold" />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
              {/*
                * How many are done, then Mark all as the list's own button, the width of the list (owner, 2026-10-16:
                * "I really hate how the mark all aligns"; it was a link at the far right of the count). Once all are
                * done it stays, quiet, ticked, so nothing under it moves.
                */}
              <button
                type="button"
                onClick={() => onMark({ action: 'done' })}
                disabled={!canTick || done}
                className="mt-3 flex min-h-11 w-full items-center justify-center gap-2 rounded-full border border-primary/40 px-4 text-[0.9375rem] font-semibold text-primary transition-colors hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:border-transparent disabled:bg-muted/60 disabled:text-muted-foreground"
              >
                <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
                {done ? t('todo.allMarked') : plants.markAll}
              </button>
            </section>
          )}
          {showLater && laterRow}
        </div>
      )}

      {/* Postpone's days for a When you can job (owner, 2026-10-17): the server's choices, and a day to pick within its window. */}
      {postpone && (
        <BottomPanel open={postponing} onOpenChange={setPostponing} title={postpone.title} wrapTitle>
          {postpone.sub && <p className="text-sm text-muted-foreground">{postpone.sub}</p>}
          <p className="mt-0.5 text-sm text-muted-foreground">{postpone.note}</p>
          <div className="mt-2 divide-y">
            {postpone.options.map((option) => (
              <button
                key={option.day ?? option.action}
                type="button"
                disabled={busy}
                onClick={() => {
                  setPostponing(false);
                  // When you can itself (owner, 2026-10-17: "I should be able to postpone it into when I can status"), or a day.
                  onMark(option.action === 'whenever' ? { action: 'whenever' } : { action: 'later', day: option.day });
                }}
                className="block min-h-12 w-full py-3 text-left font-semibold disabled:opacity-60"
              >
                {option.text}
                {option.sub && <span className="block text-sm font-normal text-muted-foreground">{option.sub}</span>}
              </button>
            ))}
            {/*
              * Pick a day: a date box you can see and tap, then Postpone (owner, 2026-10-17: "I can't pick a date ... You
              * click on it, you tap on it, nothing happens"; it was an invisible box over the row, which a phone ignored).
              */}
            {postpone.pick && (
            <div className="py-3">
              <label htmlFor={`${job.key}-postpone-day`} className="block font-semibold">
                {postpone.pick.text}
                {postpone.pick.sub && <span className="block text-sm font-normal text-muted-foreground">{postpone.pick.sub}</span>}
              </label>
              <div className="mt-2 flex items-center gap-2">
                <Input
                  id={`${job.key}-postpone-day`}
                  type="date"
                  min={postpone.pick.min}
                  max={postpone.pick.max}
                  value={pickedDay}
                  onChange={(event) => setPickedDay(event.target.value)}
                  disabled={busy}
                  className="h-11 min-w-0 flex-1"
                />
                <button
                  type="button"
                  disabled={busy || !pickedDay || pickedDay < postpone.pick.min || pickedDay > postpone.pick.max}
                  onClick={() => { setPostponing(false); onMark({ action: 'later', day: pickedDay }); }}
                  className="flex min-h-11 shrink-0 items-center rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                >
                  {t('todo.notToday')}
                </button>
              </div>
            </div>
            )}
          </div>
        </BottomPanel>
      )}
    </div>
  );
};
